import { useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { legalContent } from "@/content/legal";
import { FileText, Calendar, ChevronRight } from "lucide-react";

/**
 * LegalPage — Gizlilik Politikası ve Kullanım Koşulları için ortak component
 * Props:
 *   docKey: "privacy" | "terms"
 */
export function LegalPage({ docKey }) {
  const { lang, isRTL } = useLanguage();
  const data = legalContent[lang]?.[docKey] || legalContent.tr[docKey];

  // Anchor link tıklanınca smooth scroll
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [docKey]);

  if (!data) return null;

  const tocLabel = lang === "ar" ? "المحتويات" : lang === "en" ? "Contents" : "İçindekiler";

  return (
    <>
      {/* ─── Hero ─── */}
      <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-40" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-200 mb-5">
            <FileText className="w-3.5 h-3.5 text-[#22A7D1]" />
            <span className="text-xs font-bold tracking-[0.15em] text-[#22A7D1]">
              {docKey === "privacy" ? "KVKK" : "LEGAL"}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black text-slate-900 leading-[1.1]">
            {data.title}
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-500 max-w-2xl mx-auto">
            {data.subtitle}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400">
            <Calendar className="w-4 h-4" />
            <span>{data.lastUpdated}</span>
          </div>
        </div>
      </section>

      {/* ─── İçerik + İçindekiler ─── */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Sol — Sticky İçindekiler (TOC) */}
            <aside className="lg:col-span-3 order-2 lg:order-1">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-5">
                  <h2 className="text-xs font-bold tracking-[0.15em] text-slate-500 uppercase mb-4">
                    {tocLabel}
                  </h2>
                  <nav>
                    <ul className="space-y-1.5">
                      {data.sections.map((section) => (
                        <li key={section.id}>
                          <a
                            href={`#${section.id}`}
                            className="flex items-start gap-1.5 text-xs leading-snug text-slate-600 hover:text-[#22A7D1] transition-colors py-1 group"
                          >
                            <ChevronRight
                              className={`w-3 h-3 mt-0.5 shrink-0 text-slate-400 group-hover:text-[#22A7D1] transition-colors ${
                                isRTL ? "rotate-180" : ""
                              }`}
                            />
                            <span>{section.heading}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>
            </aside>

            {/* Sağ — Ana İçerik */}
            <article className="lg:col-span-9 order-1 lg:order-2">
              {/* Intro */}
              <div className="mb-10 p-6 rounded-2xl bg-sky-50/50 border border-sky-100">
                <p className="text-base leading-relaxed text-slate-700">{data.intro}</p>
              </div>

              {/* Sections */}
              <div className="space-y-10">
                {data.sections.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-24"
                    data-testid={`legal-section-${section.id}`}
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100">
                      {section.heading}
                    </h2>
                    <div className="space-y-4 text-sm sm:text-base leading-relaxed text-slate-600">
                      {section.paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                      {section.list && (
                        <ul className="space-y-2 pt-2">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <div className="mt-2 w-1.5 h-1.5 rounded-full bg-[#22A7D1] shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </section>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-16 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white">
                <h3 className="text-lg sm:text-xl font-bold mb-2">
                  {lang === "ar"
                    ? "هل لديك أسئلة؟"
                    : lang === "en"
                    ? "Have Questions?"
                    : "Sorularınız mı var?"}
                </h3>
                <p className="text-sm text-slate-300 mb-5">
                  {lang === "ar"
                    ? "فريقنا في بورصة جاهز للإجابة على أسئلتك."
                    : lang === "en"
                    ? "Our Bursa-based team is ready to answer your questions."
                    : "Bursa merkezli ekibimiz sorularınızı yanıtlamaya hazır."}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="mailto:entegre@entegrepanel.net"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-full bg-[#22A7D1] hover:bg-[#1B8FB5] transition-colors"
                  >
                    entegre@entegrepanel.net
                  </a>
                  <a
                    href="tel:+902247185400"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-full border border-slate-700 hover:border-slate-600 hover:bg-slate-800 transition-colors"
                  >
                    +90 224 718 54 00
                  </a>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
